import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

export default function SecondarySidebar({ config, isMinimized = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeItem, setActiveItem] = useState('');

  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const matchingItem = config.items.find((item) =>
      item.activePaths?.some((activePath) => {
        const activePathSegments = activePath.split('/').filter(Boolean);

        return (
          activePathSegments.length === pathSegments.length &&
          activePathSegments.every(
            (segment, index) =>
              segment.startsWith(':') || segment === pathSegments[index],
          )
        );
      }),
    );

    if (matchingItem) {
      setActiveItem(matchingItem.id);
      return;
    }

    const tab = searchParams.get('tab');
    if (tab) {
      setActiveItem(tab);
      return;
    }

    if (config.items.length > 0) {
      setActiveItem(config.items[0].id);
    }
  }, [searchParams, config.items, location.pathname]);

  const handleNavigation = (item) => {
    setActiveItem(item.id);
    navigate(item.path);
  };

  if (!config.enabled) return null;

  // Calculate exact position based on primary sidebar state
  const getRightPosition = () => {
    return isMinimized ? 'right-[240px]' : 'right-[56px]';
  };

  const getWidth = () => {
    return isMinimized ? 'w-16' : 'w-56';
  };

  return (
    <div
      className={`fixed top-0 h-full ${getRightPosition()} ${getWidth()} border-tertiary-10 z-[45] flex flex-col border-l bg-white/98 shadow-xl backdrop-blur-xl transition-all duration-300 ease-in-out dark:border-gray-700/50 dark:bg-gray-800/98 dark:shadow-2xl`}
    >
      {/* Header */}
      <div
        className={`border-tertiary-10 flex h-16 items-center justify-center border-b dark:border-gray-700/50 ${
          isMinimized ? 'px-2' : 'px-3'
        }`}
      >
        {!isMinimized && (
          <h2 className="text-tertiary-100 overflow-hidden text-xs font-bold whitespace-nowrap transition-opacity duration-200 dark:text-gray-200">
            {config.title}
          </h2>
        )}
        {isMinimized && (
          <div className="bg-primary-100 h-2 w-2 rounded-full dark:bg-blue-400" />
        )}
      </div>

      {/* Menu Items */}
      <div
        className={`flex-1 overflow-y-auto ${isMinimized ? 'px-2 py-3' : 'px-2 py-4'}`}
      >
        <nav className={isMinimized ? 'space-y-2' : 'space-y-1'}>
          {config.items.map((item) => {
            const Icon = item.icon;
            const isActive = activeItem === item.id;

            if (isMinimized) {
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item)}
                  className={`flex w-full items-center justify-center rounded-xl p-3 transition-all duration-200 ${
                    isActive
                      ? 'bg-primary-100 shadow-primary-20 text-white shadow-md dark:bg-blue-600 dark:bg-gradient-to-r dark:from-blue-600 dark:to-blue-500 dark:shadow-lg dark:shadow-blue-500/20'
                      : 'text-tertiary-60 hover:bg-tertiary-6 hover:text-tertiary-100 dark:text-gray-400 dark:hover:bg-gray-700/50 dark:hover:text-white'
                  }`}
                >
                  <Icon size={20} />
                </button>
              );
            }

            return (
              <button
                key={item.id}
                onClick={() => handleNavigation(item)}
                className={`group relative flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2.5 transition-all duration-200 ${
                  isActive
                    ? 'bg-primary-100 shadow-primary-20 text-white shadow-md dark:bg-blue-600 dark:bg-gradient-to-r dark:from-blue-600 dark:to-blue-500 dark:shadow-lg dark:shadow-blue-500/20'
                    : 'text-tertiary-80 hover:bg-tertiary-6 hover:text-tertiary-100 dark:text-gray-300 dark:hover:bg-gray-700/50 dark:hover:text-white'
                }`}
              >
                {/* Active Indicator Bar */}
                {isActive && (
                  <div className="bg-secondary-100 absolute top-1/2 right-0 h-6 w-1 -translate-y-1/2 rounded-l-full dark:bg-blue-400 dark:bg-gradient-to-b dark:from-blue-400 dark:to-blue-300" />
                )}

                <div className="flex h-8 w-8 items-center justify-center">
                  <Icon size={18} />
                </div>

                <div className="flex-1 text-right">
                  <div
                    className={`text-xs font-semibold transition-colors ${
                      isActive
                        ? 'text-white'
                        : 'text-tertiary-80 group-hover:text-tertiary-100 dark:text-gray-300 dark:group-hover:text-white'
                    }`}
                  >
                    {item.label}
                  </div>
                  <div
                    className={`text-[10px] transition-colors ${
                      isActive
                        ? 'text-white/80'
                        : 'text-tertiary-60 group-hover:text-tertiary-80 dark:text-gray-500 dark:group-hover:text-gray-400'
                    }`}
                  >
                    {item.description}
                  </div>
                </div>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
